import {
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type HTMLAttributes,
  type PointerEvent as ReactPointerEvent,
  type RefCallback,
  type ReactNode,
} from "react";
import { classNames } from "../lib/format";

type EffectElement = "div" | "section" | "article" | "li" | "span";

type EffectElementProps = Omit<HTMLAttributes<HTMLElement>, "children"> & {
  as?: EffectElement;
  children: ReactNode;
};

type EffectStyle = CSSProperties & Record<`--${string}`, string | number>;

export type RevealProps = EffectElementProps & {
  delayMs?: number;
  rootMargin?: string;
  threshold?: number;
};

const revealClass =
  "transition-[opacity,transform,filter] duration-500 ease-out [transition-delay:var(--reveal-delay,0ms)] data-[reveal-state=pending]:translate-y-4 data-[reveal-state=pending]:opacity-0 data-[reveal-state=pending]:blur-[3px] data-[reveal-state=pending]:will-change-[opacity,transform,filter] motion-reduce:transform-none motion-reduce:opacity-100 motion-reduce:blur-none motion-reduce:transition-none";

/**
 * Progressively enhances already-visible markup with a one-time entrance.
 * The server output has no hidden state, so content remains available when
 * JavaScript, IntersectionObserver or animation support is absent.
 */
export function Reveal({
  as = "div",
  children,
  className,
  delayMs = 0,
  rootMargin = "0px 0px -10% 0px",
  threshold = 0.12,
  style,
  ...props
}: RevealProps) {
  const elementRef = useRef<HTMLElement | null>(null);
  const captureElement = useCallback<RefCallback<HTMLElement>>((element) => {
    elementRef.current = element;
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const motionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (motionQuery?.matches || typeof IntersectionObserver === "undefined") {
      element.dataset.revealState = "visible";
      return;
    }

    const bounds = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;
    const startsInViewport =
      bounds.bottom > 0 &&
      bounds.top < viewportHeight &&
      bounds.right > 0 &&
      bounds.left < viewportWidth;

    if (startsInViewport) {
      element.dataset.revealState = "visible";
      return;
    }

    let observer: IntersectionObserver | null = null;
    const show = () => {
      element.dataset.revealState = "visible";
      observer?.unobserve(element);
    };
    const handleMotionPreference = (event: MediaQueryListEvent) => {
      if (event.matches) show();
    };

    element.dataset.revealState = "pending";
    observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) show();
      },
      { rootMargin, threshold },
    );
    observer.observe(element);
    motionQuery?.addEventListener?.("change", handleMotionPreference);

    return () => {
      observer?.disconnect();
      motionQuery?.removeEventListener?.("change", handleMotionPreference);
    };
  }, [rootMargin, threshold]);

  const revealStyle: EffectStyle = {
    ...style,
    "--reveal-delay": `${Math.max(0, delayMs)}ms`,
  };

  const resolvedClassName = classNames(revealClass, className);

  switch (as) {
    case "article":
      return (
        <article
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          style={revealStyle}
        >
          {children}
        </article>
      );
    case "li":
      return (
        <li
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          style={revealStyle}
        >
          {children}
        </li>
      );
    case "section":
      return (
        <section
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          style={revealStyle}
        >
          {children}
        </section>
      );
    case "span":
      return (
        <span
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          style={revealStyle}
        >
          {children}
        </span>
      );
    default:
      return (
        <div
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          style={revealStyle}
        >
          {children}
        </div>
      );
  }
}

export type SpotlightSurfaceProps = EffectElementProps & {
  spotlightColor?: string;
};

const spotlightClass =
  "relative isolate overflow-hidden before:pointer-events-none before:absolute before:inset-0 before:z-[3] before:bg-[radial-gradient(circle_at_var(--spotlight-x)_var(--spotlight-y),var(--spotlight-color)_0%,transparent_48%)] before:opacity-0 before:transition-opacity before:duration-200 before:content-[''] data-[spotlight-state=enabled]:hover:before:opacity-100 data-[spotlight-state=enabled]:focus-within:before:opacity-100 motion-reduce:before:opacity-0";

/**
 * Adds a restrained pointer-following highlight without scheduling React
 * renders. Coarse pointers and reduced-motion environments stay inert.
 */
export function SpotlightSurface({
  as = "div",
  children,
  className,
  onPointerMove,
  spotlightColor = "rgba(216, 183, 242, 0.14)",
  style,
  ...props
}: SpotlightSurfaceProps) {
  const elementRef = useRef<HTMLElement | null>(null);
  const enabledRef = useRef(false);
  const captureElement = useCallback<RefCallback<HTMLElement>>((element) => {
    elementRef.current = element;
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const motionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const pointerQuery = window.matchMedia?.("(pointer: coarse)");
    const syncCapability = () => {
      const enabled = !motionQuery?.matches && !pointerQuery?.matches;
      enabledRef.current = enabled;
      element.dataset.spotlightState = enabled ? "enabled" : "disabled";
    };

    syncCapability();
    motionQuery?.addEventListener?.("change", syncCapability);
    pointerQuery?.addEventListener?.("change", syncCapability);

    return () => {
      motionQuery?.removeEventListener?.("change", syncCapability);
      pointerQuery?.removeEventListener?.("change", syncCapability);
    };
  }, []);

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    onPointerMove?.(event);
    if (!enabledRef.current) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    event.currentTarget.style.setProperty(
      "--spotlight-x",
      `${event.clientX - bounds.left}px`,
    );
    event.currentTarget.style.setProperty(
      "--spotlight-y",
      `${event.clientY - bounds.top}px`,
    );
  };

  const spotlightStyle: EffectStyle = {
    ...style,
    "--spotlight-x": "50%",
    "--spotlight-y": "50%",
    "--spotlight-color": spotlightColor,
  };

  const resolvedClassName = classNames(spotlightClass, className);

  switch (as) {
    case "article":
      return (
        <article
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          onPointerMove={handlePointerMove}
          style={spotlightStyle}
        >
          {children}
        </article>
      );
    case "li":
      return (
        <li
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          onPointerMove={handlePointerMove}
          style={spotlightStyle}
        >
          {children}
        </li>
      );
    case "section":
      return (
        <section
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          onPointerMove={handlePointerMove}
          style={spotlightStyle}
        >
          {children}
        </section>
      );
    case "span":
      return (
        <span
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          onPointerMove={handlePointerMove}
          style={spotlightStyle}
        >
          {children}
        </span>
      );
    default:
      return (
        <div
          {...props}
          ref={captureElement}
          className={resolvedClassName}
          onPointerMove={handlePointerMove}
          style={spotlightStyle}
        >
          {children}
        </div>
      );
  }
}
